import { cn } from '@/utils';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  className?: string;
}

export function Badge({ children, variant = 'default', size = 'md', className }: BadgeProps) {
  const variants: Record<BadgeVariant, string> = {
    default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-amber-900/20 dark:text-amber-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </span>
  );
}

// Status badge helper for common statuses
// eslint-disable-next-line react-refresh/only-export-components
export function getStatusBadgeVariant(status: string): BadgeVariant {
  const statusMap: Record<string, BadgeVariant> = {
    // Policy statuses
    active: 'success',
    issued: 'success',
    draft: 'default',
    pending_payment: 'warning',
    payment_received: 'info',
    pending_issuance: 'warning',
    lapsed: 'error',
    cancelled: 'error',
    expired: 'default',
    // Policy display_status (date-based derivation — round-2 Wave A, item 9)
    inactive: 'default',
    // B9 lifecycle: cover not started yet (active/expired/cancelled above)
    upcoming: 'info',
    // Payment statuses
    completed: 'success',
    pending: 'warning',
    partial: 'warning',
    refunded: 'info',
    failed: 'error',
    // Quote statuses
    submitted: 'info',
    processing: 'warning',
    // KYC statuses
    verified: 'success',
    under_review: 'warning',
    rejected: 'error',
    // DQ-578: earlier verification no longer stands; needs attention, not "no status"
    reverify_required: 'warning',
    // Customer segments
    vip: 'primary',
    premium: 'info',
    high_value: 'success',
    standard: 'default',
    corporate: 'primary',
  };

  return statusMap[(status || '').toLowerCase()] || 'default';
}
